
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { Actions, ButtonLink, IconNewWindow } from 'braid-design-system';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "previewing-a-job-ad"
    }}>{`Previewing a job ad`}</h1>
    <p>{`The SEEK API provides the ability for a hirer to preview a job ad as it would appear on SEEK.`}</p>
    <p>{`By integrating job ad preview into your posting flow, hirers will be able to:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Preview how their job ad will display in the SEEK search results and job details page.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`See how their job ad will look on desktop and mobile devices.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Verify that their company `}<a parentName="p" {...{
            "href": "/use-cases/job-posting/branding"
          }}>{`logo and cover image`}</a>{` is as expected.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Spot issues with their job ad prior to posting, reducing the need to update their job ad later.`}</p>
      </li>
    </ul>
    <p>{`A job ad preview can be programmatically retrieved by your software when `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/managing-job-ads/posting-a-job-ad"
      }}>{`posting`}</a>{` or `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/managing-job-ads/updating-a-job-ad"
      }}>{`updating`}</a>{` a job ad.
This allows you to build your own button, write your own query to the SEEK API and choose how it is handled.`}</p>

    <Actions mdxType="Actions">
  <ButtonLink href="https://job-ad-preview.seek.com/anzPublicTest?id=seekAnzPublicTest:postedPositionProfilePreview:seekApi:37EWfWxhwVEGTT5BcjfE2L" icon={<IconNewWindow mdxType="IconNewWindow" />} size="small" target="_blank" tone="brandAccent" mdxType="ButtonLink">
    Try it out
  </ButtonLink>
    </Actions>
    <h2 {...{
      "id": "browser-support"
    }}>{`Browser support`}</h2>
    <p>{`The Job Ad Preview page tracks our standard `}<a parentName="p" {...{
        "href": "/introduction/browser-support"
      }}>{`browser support policy`}</a>{`.`}</p>
    <h2 {...{
      "id": "before-you-begin"
    }}>{`Before you begin`}</h2>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`SEEK recommends using a `}<a parentName="p" {...{
            "href": "/auth/browser-tokens"
          }}>{`browser token`}</a>{` to generate a job ad preview directly from a hirer’s browser.
For the browser token to work, you will need to include the `}<inlineCode parentName="p">{`query:posted-position-profile-previews`}</inlineCode>{` scope in your request.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`You can also generate a job ad preview URL for a hirer from your backend using a `}<a parentName="p" {...{
            "href": "/auth/partner-tokens"
          }}>{`partner token`}</a>{`.`}</p>
      </li>
    </ul>
    <h2 {...{
      "id": "building-job-ad-preview"
    }}>{`Building job ad preview`}</h2>
    <p>{`Your implementation will need to:`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Display a button for hirers to click on to preview the job ad.`}</p>
        <p parentName="li">{`The button should only be enabled if the hirer has provided a `}<inlineCode parentName="p">{`positionTitle`}</inlineCode>{` and a `}<inlineCode parentName="p">{`seekAnzWorkTypeCode`}</inlineCode>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Execute the `}<a parentName="p" {...{
            "href": "/schema/query/postedPositionProfilePreview"
          }}><inlineCode parentName="a">{`postedPositionProfilePreview`}</inlineCode></a>{` query to generate a job ad preview when the button is clicked.`}</p>
        <p parentName="li">{`Your software must provide the `}<a parentName="p" {...{
            "href": "/schema/named-type/PostedPositionProfilePreview_PositionProfileInput"
          }}>{`state of the job ad`}</a>{` to the `}<a parentName="p" {...{
            "href": "/schema/query/postedPositionProfilePreview#/argument/positionProfile"
          }}><inlineCode parentName="a">{`positionProfile`}</inlineCode>{` argument`}</a>{`.
It should match what you eventually post for an accurate preview.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`With the `}<a parentName="p" {...{
            "href": "/schema/named-type/PostedPositionProfilePreview#/field/previewUri"
          }}><inlineCode parentName="a">{`previewUri`}</inlineCode>{` field`}</a>{` from the query response, open a new tab for the hirer or render an `}<inlineCode parentName="p">{`<iframe />`}</inlineCode>{` in flow.`}</p>
        <p parentName="li">{`The URI is valid for 24 hours.`}</p>
      </li>
    </ol>
    <h3 {...{
      "id": "example-query"
    }}>{`Example query`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query ($positionProfile: PostedPositionProfilePreview_PositionProfileInput!) {\\n  postedPositionProfilePreview(positionProfile: $positionProfile) {\\n    previewUri {\\n      url\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":69,\"column\":1,\"offset\":2660},\"end\":{\"line\":77,\"column\":4,\"offset\":2860},\"indent\":[1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"positionProfile\\\": {\\n    \\\"positionTitle\\\": \\\"Senior Developer\\\",\\n    \\\"positionOrganizations\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n    \\\"jobCategories\\\": \\\"seekAnzPublicTest:jobCategory:seek:2EFstqFvP\\\",\\n    \\\"positionLocation\\\": \\\"seekAnzPublicTest:location:seek:2FqwWaaMV\\\",\\n    \\\"offeredRemunerationPackage\\\": {\\n      \\\"descriptions\\\": [\\\"Base + Super + Profit Share\\\"],\\n      \\\"basisCode\\\": \\\"Hourly\\\",\\n      \\\"ranges\\\": [\\n        {\\n          \\\"intervalCode\\\": \\\"Hour\\\",\\n          \\\"maximumAmount\\\": {\\n            \\\"currency\\\": \\\"AUD\\\",\\n            \\\"value\\\": 200\\n          },\\n          \\\"minimumAmount\\\": {\\n            \\\"currency\\\": \\\"AUD\\\",\\n            \\\"value\\\": 100\\n          }\\n        }\\n      ]\\n    },\\n    \\\"postingInstructions\\\": [\\n      {\\n        \\\"seekAdvertisementProductId\\\": \\\"globalPublicTest:adProduct:seekApi:EWHKTY9D6mJFiA5EH6P7MFbByJ1JVteAQQkfa3uMJLPJ\\\",\\n        \\\"brandingId\\\": \\\"globalPublicTest:advertisementBranding:hirerBranding:5DU2L2vQsyTXuu9NEUKT3f\\\"\\n      }\\n    ],\\n    \\\"positionFormattedDescriptions\\\": [\\n      {\\n        \\\"descriptionId\\\": \\\"AdvertisementDetails\\\",\\n        \\\"content\\\": \\\"My job ad details\\\"\\n      },\\n      {\\n        \\\"descriptionId\\\": \\\"SearchSummary\\\",\\n        \\\"content\\\": \\\"Search summary content\\\"\\n      },\\n      {\\n        \\\"descriptionId\\\": \\\"SearchBulletPoint\\\",\\n        \\\"content\\\": \\\"Search bullet point 1\\\"\\n      },\\n      {\\n        \\\"descriptionId\\\": \\\"SearchBulletPoint\\\",\\n        \\\"content\\\": \\\"Search bullet point 2\\\"\\n      },\\n      {\\n        \\\"descriptionId\\\": \\\"SearchBulletPoint\\\",\\n        \\\"content\\\": \\\"Search bullet point 3\\\"\\n      }\\n    ],\\n    \\\"seekAnzWorkTypeCode\\\": \\\"FullTime\\\",\\n    \\\"seekApplicationQuestionnaireId\\\": \\\"seekAnzPublicTest:applicationQuestionnaire:rrv2:SqFmkV8S2dMipyqbHjD9Mr\\\",\\n    \\\"seekVideo\\\": {\\n      \\\"url\\\": \\\"https://www.youtube.com/watch?v=vsLYc3gzMzw\\\",\\n      \\\"seekAnzPositionCode\\\": \\\"Below\\\"\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":79,\"column\":1,\"offset\":2862},\"end\":{\"line\":139,\"column\":4,\"offset\":4679},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":\"lovato-ignore-runnable-graphql-operation\",\"value\":\"{\\n  \\\"postedPositionProfilePreview\\\": {\\n    \\\"previewUri\\\": {\\n      \\\"url\\\": \\\"https://job-ad-preview.seek.com/anzPublicTest?id=seekAnzPublicTest:postedPositionProfilePreview:seekApi:37EWfWxhwVEGTT5BcjfE2L\\\"\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":141,\"column\":1,\"offset\":4681},\"end\":{\"line\":149,\"column\":4,\"offset\":4945},\"indent\":[1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "($positionProfile:": true,
        "PostedPositionProfilePreview_PositionProfileInput!)": true,
        "{\\n": true,
        "": true,
        "postedPositionProfilePreview(positionProfile:": true,
        "$positionProfile)": true,
        "previewUri": true,
        "url\\n": true,
        "}\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":69,\"column\":1,\"offset\":2660},\"end\":{\"line\":77,\"column\":4,\"offset\":2860},\"indent\":[1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"positionProfile\\\":": true,
        "\\\"positionTitle\\\":": true,
        "\\\"Senior": true,
        "Developer\\\",\\n": true,
        "\\\"positionOrganizations\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n": true,
        "\\\"jobCategories\\\":": true,
        "\\\"seekAnzPublicTest:jobCategory:seek:2EFstqFvP\\\",\\n": true,
        "\\\"positionLocation\\\":": true,
        "\\\"seekAnzPublicTest:location:seek:2FqwWaaMV\\\",\\n": true,
        "\\\"offeredRemunerationPackage\\\":": true,
        "\\\"descriptions\\\":": true,
        "[\\\"Base": true,
        "+": true,
        "Super": true,
        "Profit": true,
        "Share\\\"],\\n": true,
        "\\\"basisCode\\\":": true,
        "\\\"Hourly\\\",\\n": true,
        "\\\"ranges\\\":": true,
        "[\\n": true,
        "\\\"intervalCode\\\":": true,
        "\\\"Hour\\\",\\n": true,
        "\\\"maximumAmount\\\":": true,
        "\\\"currency\\\":": true,
        "\\\"AUD\\\",\\n": true,
        "\\\"value\\\":": true,
        "200\\n": true,
        "},\\n": true,
        "\\\"minimumAmount\\\":": true,
        "100\\n": true,
        "]\\n": true,
        "\\\"postingInstructions\\\":": true,
        "\\\"seekAdvertisementProductId\\\":": true,
        "\\\"globalPublicTest:adProduct:seekApi:EWHKTY9D6mJFiA5EH6P7MFbByJ1JVteAQQkfa3uMJLPJ\\\",\\n": true,
        "\\\"brandingId\\\":": true,
        "\\\"globalPublicTest:advertisementBranding:hirerBranding:5DU2L2vQsyTXuu9NEUKT3f\\\"\\n": true,
        "],\\n": true,
        "\\\"positionFormattedDescriptions\\\":": true,
        "\\\"descriptionId\\\":": true,
        "\\\"AdvertisementDetails\\\",\\n": true,
        "\\\"content\\\":": true,
        "\\\"My": true,
        "job": true,
        "ad": true,
        "details\\\"\\n": true,
        "\\\"SearchSummary\\\",\\n": true,
        "\\\"Search": true,
        "summary": true,
        "content\\\"\\n": true,
        "\\\"SearchBulletPoint\\\",\\n": true,
        "bullet": true,
        "point": true,
        "1\\\"\\n": true,
        "2\\\"\\n": true,
        "3\\\"\\n": true,
        "\\\"seekAnzWorkTypeCode\\\":": true,
        "\\\"FullTime\\\",\\n": true,
        "\\\"seekApplicationQuestionnaireId\\\":": true,
        "\\\"seekAnzPublicTest:applicationQuestionnaire:rrv2:SqFmkV8S2dMipyqbHjD9Mr\\\",\\n": true,
        "\\\"seekVideo\\\":": true,
        "\\\"url\\\":": true,
        "\\\"https://www.youtube.com/watch?v": "vsLYc3gzMzw\\\",\\n",
        "\\\"seekAnzPositionCode\\\":": true,
        "\\\"Below\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":79,\"column\":1,\"offset\":2862},\"end\":{\"line\":139,\"column\":4,\"offset\":4679},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":\"lovato-ignore-runnable-graphql-operation\",\"value\":\"{\\n": true,
        "\\\"postedPositionProfilePreview\\\":": true,
        "\\\"previewUri\\\":": true,
        "\\\"https://job-ad-preview.seek.com/anzPublicTest?id": "seekAnzPublicTest:postedPositionProfilePreview:seekApi:37EWfWxhwVEGTT5BcjfE2L\\\"\\n",
        "}\\n}\",\"position\":{\"start\":{\"line\":141,\"column\":1,\"offset\":4681},\"end\":{\"line\":149,\"column\":4,\"offset\":4945},\"indent\":[1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($positionProfile: PostedPositionProfilePreview_PositionProfileInput!) {
  postedPositionProfilePreview(positionProfile: $positionProfile) {
    previewUri {
      url
    }
  }
}
`}</code></pre>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;